import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface Part {
  value: string;
  label: string;
  category: string;
}

interface PartsAutocompleteProps {
  value: string;
  onChange: (partNumber: string, partName: string) => void;
  placeholder: string;
  disabled?: boolean;
  availableParts: Part[];
  searchBy: 'partNumber' | 'partName';
}

const PartsAutocomplete: React.FC<PartsAutocompleteProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  availableParts,
  searchBy
}) => {
  const { isDarkMode } = useTheme();
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredParts, setFilteredParts] = useState<Part[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Optional debug logging
  // console.log('🔄 PartsAutocomplete render:', { searchBy, isOpen, filteredPartsCount: filteredParts.length });

  // Update input value when prop value changes
  useEffect(() => {
    console.log('🔄 PartsAutocomplete value prop changed:', { 
      oldInputValue: inputValue, 
      newValue: value,
      searchBy 
    });
    setInputValue(value);
  }, [value]);



  // Filter parts based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredParts([]);
      setIsOpen(false);
      return;
    }

    const query = inputValue.toLowerCase();
    const filtered = availableParts.filter(part => {
      if (searchBy === 'partNumber') {
        return part.value.toLowerCase().includes(query);
      } else {
        // Search part name - extract name from label
        const partName = part.label.includes(' - ') 
          ? part.label.split(' - ').slice(1).join(' - ')
          : part.label;
        return partName.toLowerCase().includes(query) || 
               part.value.toLowerCase().includes(query); // Also allow searching by part number
      }
    }).slice(0, 8); // Limit to 8 results for better UX

    setFilteredParts(filtered);
    // Only show dropdown if field is focused and there are results
    if (filtered.length > 0 && isFocused) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }, [inputValue, availableParts, searchBy, isFocused]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Handle part selection
  const selectPart = (part: Part) => {
    console.log('🎯 selectPart called with:', part);
    
    const partName = part.label.includes(' - ') 
      ? part.label.split(' - ').slice(1).join(' - ')
      : part.label;

    console.log('🎯 Part selection details:', { 
      partNumber: part.value, 
      partName, 
      searchBy,
      originalLabel: part.label,
      currentInputValue: inputValue
    });

    try {
      if (searchBy === 'partNumber') {
        console.log('🔄 Setting input value to part number:', part.value);
        setInputValue(part.value);
        console.log('🔄 Calling onChange with:', { partNumber: part.value, partName });
        onChange(part.value, partName);
      } else {
        console.log('🔄 Setting input value to part name:', partName);
        setInputValue(partName);
        console.log('🔄 Calling onChange with:', { partNumber: part.value, partName });
        onChange(part.value, partName);
      }
      
      console.log('✅ Part selection completed, closing dropdown');
      setIsOpen(false);
      setIsFocused(false);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('❌ Error in selectPart:', error);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredParts.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredParts[highlightedIndex]) {
          selectPart(filteredParts[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle click outside to close dropdown and update position on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
      }
    };

    const handleScrollOrResize = () => {
      // No positioning needed for inline dropdown
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);



  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
          if (inputValue.trim() && filteredParts.length > 0) {
            setIsOpen(true);
          }
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        disabled={disabled}
        placeholder={placeholder}
        title={inputValue && inputValue.trim() ? inputValue : undefined}
        className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors duration-150 ${
          disabled 
            ? isDarkMode ? 'bg-gray-800 cursor-not-allowed text-gray-400' : 'bg-gray-50 cursor-not-allowed'
            : isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white border-gray-300'
        }`}
      />
      
      {/* Inline dropdown */}
      {isOpen && filteredParts.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute w-full mt-1 border rounded-md shadow-xl z-50 max-h-64 overflow-y-auto ${
            isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
          }`}
          style={{
            minWidth: '250px'
          }}
        >
          {filteredParts.map((part, index) => {
            const partName = part.label.includes(' - ') 
              ? part.label.split(' - ').slice(1).join(' - ')
              : part.label;

            return (
              <div
                key={part.value}
                title={`${part.value} - ${partName}`}
                onMouseEnter={() => {
                  setHighlightedIndex(index);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🖱️ Dropdown item selected:', part.value);
                  selectPart(part);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectPart(part);
                }}
                className={`px-3 py-1.5 cursor-pointer text-sm border-b last:border-b-0 transition-colors duration-150 ${
                  index === highlightedIndex
                    ? isDarkMode ? 'bg-blue-700 text-white' : 'bg-blue-100 text-blue-900'
                    : isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700 border-gray-600' 
                      : 'text-gray-700 hover:bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{part.value}</div>
                    <div className={`text-xs truncate ${
                      index === highlightedIndex
                        ? isDarkMode ? 'text-blue-200' : 'text-blue-700'
                        : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {partName}
                    </div>
                  </div>
                  <div className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 transition-colors duration-150 ${
                    index === highlightedIndex
                      ? isDarkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-200 text-blue-800'
                      : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {part.category}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartsAutocomplete; 